import { alpha, createTheme, IconButtonProps } from "@mui/material";

declare module "@mui/material/Badge/Badge" {
  interface BadgePropsColorOverrides {
    grey: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonOwnProps {
    variant?: "text" | "contained" | "outlined";
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    paper: true;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    grey: string;
    disabled: string;
    primary: string;
    secondary: string;
    error: string;
    info: string;
    success: string;
    warning: string;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface CommonColors {
    grey: string;
  }
}

// Change default colors by creating a new theme
let theme = createTheme({
  palette: {
    primary: {
      main: "#408f93",
      light: "#66a5a8",
      dark: "#2c6466",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ffa700",
      light: "#ffb833",
      dark: "#b27400",
      contrastText: "#000000",
    },
    common: {
      black: "#000000",
      white: "#ffffff",
      grey: "#6c757d",
    },
    background: {
      default: "#e4ebf1",
      paper: "#ffffff",
      disabled: "#ebebeb",
      primary: "#e4f0f1",
      secondary: "#f1ede4",
      error: "#fde2e7",
      info: "#e4ebf1",
      success: "#e4f1e5",
      warning: "#f7f0e3",
    },
  },
  typography: {
    fontFamily: "Avenir Next,sans-serif",
  },
  breakpoints: {
    values: {
      xs: 350,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

theme = createTheme(theme, {
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          top: "0px",
          height: "calc(3.5rem + 2px)",
          border: "1px solid #ced4da",
          fontFamily: "Avenir Next",
          color: "green",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          flexGrow: 1,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          variants: [
            {
              props: { color: "grey" },
              style: {
                color: "white",
                backgroundColor: theme.palette.grey[500],
              },
            },
          ],
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiDialog-paper": {
            margin: "1rem",
            width: "100%",
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          [theme.breakpoints.down("md")]: {
            paddingBlock: "0.75em",
            paddingInline: "1.5em",
            "&.MuiButton-contained, &.MuiButton-outlined": {
              borderRadius: "100vh",
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "contained" },
              style: (props: IconButtonProps) => {
                const color = (
                  props.color === "error"
                  || props.color === "info"
                  || props.color === "success"
                  || props.color === "warning"
                  || props.color === "primary"
                  || props.color === "secondary"
                ) ? props.color : "primary";

                return {
                  backgroundColor: theme.palette[color].main,
                  color: theme.palette[color].contrastText,
                  boxShadow:
                    "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)",
                  transition: theme.transitions.create([
                    "border-color",
                    "background-color",
                    "box-shadow",
                  ]),
                  "&:hover": {
                    backgroundColor: theme.palette[color].dark,
                    boxShadow:
                      "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: theme.palette[color].main,
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
                  },
                }
              },
            },
            {
              props: { variant: "contained", color: "paper" },
              style: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: "#f8f8f8",
                },
                "&.Mui-focused": {
                  backgroundColor: theme.palette.background.paper,
                },
              }
            },
            {
              props: { variant: "outlined" },
              style: (props: IconButtonProps) => {
                const color = (
                  props.color === "error"
                  || props.color === "info"
                  || props.color === "success"
                  || props.color === "warning"
                  || props.color === "primary"
                  || props.color === "secondary"
                ) ? props.color : "primary";

                return {
                  backgroundColor: "transparent",
                  color: theme.palette[color].main,
                  border: `1px solid ${theme.palette[color].light}`,
                  boxShadow: "none",
                  transition: theme.transitions.create([
                    "border-color",
                    "background-color",
                    "box-shadow",
                  ]),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette[color].main, 0.05),
                    borderColor: theme.palette[color].dark,
                    color: theme.palette[color].dark,
                  },
                  "&.Mui-focused": {
                    backgroundColor: theme.palette[color].main,
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
                  },
                }
              },
            },
          ],
        },
      },
    },
  },
});

export default theme;
